import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Check, Loader2, Ban, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import TwoFactorBlock from "@/components/TwoFactorBlock";
import { PROFILE } from "@/constants/testIds";
import { GoogleGlyph } from "./ProfileBits";
import TripsSection from "./edit/TripsSection";
import AmbassadorSection from "./edit/AmbassadorSection";
import NotificationsSection from "./edit/NotificationsSection";
import VipBadgeVisibilityRow from "./edit/VipBadgeVisibilityRow";
import { isNativeIOS } from "@/lib/nativePush";
import {
  Section, Row, Toggle, InlineInput, InlineSelect, ChipMultiSelect, SectionIcons,
  BODY_TYPES, POSITIONS, ETHNICITIES, RELATIONSHIP_STATUSES, GENDERS, PRONOUNS,
  TRIBES, LOOKING_FOR_OPTIONS, MEET_AT_OPTIONS, HIV_STATUSES,
} from "./edit/ProfileFieldPrimitives";

/**
 * ProfileEditForm — Grindr-parity sectioned editor.
 *
 * Sections mirror the mobile app:
 *  • Basics          — display name, avatar, bio
 *  • Desires         — curated tag picker (unchanged)
 *  • Tags            — free-text keywords
 *  • Stats           — show-age toggle, age, height, weight, body-type,
 *                      show-position toggle, position, ethnicity, relationship
 *  • Preferences     — tribes (show + mine + into), looking-for, meet-at, NSFW opt-in
 *  • Identity        — gender, pronouns
 *  • Health          — HIV status + last-tested date
 *  • Social Links    — instagram / X / facebook / spotify
 *  • Security        — 2FA + Google link (kept from previous form)
 *
 * The parent (`Profile.jsx`) owns state and passes `form` + `onFormChange`.
 */
export default function ProfileEditForm({
  form, onFormChange,
  desiresCatalog,
  avatarInputRef, avatarUploading,
  profile, onSave, onCancel, onTwoFactorToggled,
}) {
  const set = (patch) => onFormChange({ ...form, ...patch });
  const toggleDesire = (tag) => {
    if (form.desires.includes(tag)) {
      set({ desires: form.desires.filter((t) => t !== tag) });
    } else if (form.desires.length < 15) {
      set({ desires: [...form.desires, tag] });
    }
  };

  return (
    <div className="space-y-2" data-testid="profile-edit-form">
      {/* ---------------- BASICS ---------------- */}
      <div className="space-y-4">
        <div data-testid="profile-preferred-name-block">
          <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
            <span>Preferred name <span className="text-zinc-600 normal-case tracking-normal">· shown to everyone</span></span>
            <span data-testid="profile-preferred-name-counter" className={`text-[10px] normal-case tracking-normal ${(form.display_name || "").trim().length > 30 || (form.display_name || "").trim().length < 2 ? "text-rose-400" : "text-zinc-600"}`}>{(form.display_name || "").trim().length}/30</span>
          </label>
          <Input
            data-testid={PROFILE.displayNameInput}
            value={form.display_name}
            onChange={(e) => set({ display_name: e.target.value })}
            className="mt-2 bg-black/40 border-white/10 rounded-none py-6"
            maxLength={30}
            placeholder="How members will see you"
          />
          <p className="mt-2 text-xs text-zinc-500 leading-relaxed" data-testid="profile-preferred-name-help">
            2–30 characters — letters, numbers, spaces, emoji and basic punctuation. Names that are offensive, reserved, or imply staff/official status are rejected. You can change it up to 3 times a day; every change is recorded in your account history and you'll get a notification.
          </p>
          {profile.preferred_name_auto && (
            <p className="mt-2 text-xs text-amber-300" data-testid="profile-preferred-name-auto-note">This is a temporary auto-generated name — pick your own.</p>
          )}
        </div>

        <div data-testid="profile-internal-handle-block">
          <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
            <span>Account handle</span>
            <span className="text-[10px] text-zinc-600 normal-case tracking-normal" data-testid="profile-username-locked-note">🔒 private · not editable</span>
          </label>
          <div data-testid="profile-username-readonly" className="mt-2 bg-black/20 border border-white/5 rounded-none py-4 px-3 text-zinc-500 font-mono select-all">
            @{profile.handle}
          </div>
          <p className="mt-2 text-xs text-zinc-500 leading-relaxed">Used only for sign-in, your profile link, referrals and account safety. Other members never see it — they see your preferred name instead.</p>
        </div>

        <div data-testid="profile-side-block">
          <label className="text-xs uppercase tracking-widest text-zinc-500">Your side</label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => set({ side: "lgbtq" })}
              data-testid="profile-side-lgbtq"
              className={`px-4 py-3 border text-sm transition-colors text-left ${
                form.side === "lgbtq"
                  ? "border-rose-500 bg-rose-950/40 text-white"
                  : "border-white/10 bg-black/40 hover:border-white/20 text-zinc-300"
              }`}
            >
              <div className="font-medium">LGBTQ side</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Queer creators + communities</div>
            </button>
            <button
              type="button"
              onClick={() => set({ side: "straight" })}
              data-testid="profile-side-straight"
              className={`px-4 py-3 border text-sm transition-colors text-left ${
                form.side === "straight"
                  ? "border-rose-500 bg-rose-950/40 text-white"
                  : "border-white/10 bg-black/40 hover:border-white/20 text-zinc-300"
              }`}
            >
              <div className="font-medium">Straight side</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Straight creators + communities</div>
            </button>
            <button
              type="button"
              onClick={() => set({ side: "explore" })}
              data-testid="profile-side-explore"
              className={`px-4 py-3 border text-sm transition-colors text-left ${
                form.side === "explore"
                  ? "border-amber-400 bg-amber-950/30 text-white"
                  : "border-white/10 bg-black/40 hover:border-white/20 text-zinc-300"
              }`}
            >
              <div className="font-medium">Explore Your Way</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Both sides · filter by gender & interest</div>
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
            Switching sides changes what shows up in your feed, live tab,
            communities, and Nearby. Content you've already posted stays where
            it was published.
          </p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500">Profile picture</label>
          <div className="mt-2 flex items-center gap-3">
            <Avatar className="w-14 h-14 border border-white/10">
              <AvatarImage src={form.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white">
                {profile.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <Button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              variant="ghost"
              data-testid="profile-edit-upload-button"
              className="rounded-none border border-white/15 hover:border-rose-500/60 hover:bg-rose-950/30 text-sm gap-2"
            >
              {avatarUploading
                ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                : <><Camera size={14} /> Upload new</>}
            </Button>
          </div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-600 mt-3 block">Or paste an image URL</label>
          <Input
            value={form.avatar_url}
            onChange={(e) => set({ avatar_url: e.target.value })}
            className="mt-1 bg-black/40 border-white/10 rounded-none py-6"
            placeholder="https://…"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
            <span>About me</span>
            <span className="text-[10px] text-zinc-600 normal-case tracking-normal">
              {(form.bio || "").length}/500
            </span>
          </label>
          <Textarea
            data-testid={PROFILE.bioInput}
            value={form.bio}
            onChange={(e) => set({ bio: e.target.value.slice(0, 500) })}
            className="mt-2 bg-black/40 border-white/10 rounded-none min-h-24"
          />
        </div>
      </div>

      {/* ---------------- DESIRES (existing) ---------------- */}
      <div className="pt-2">
        <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>Desires & Fantasies</span>
          <span className="text-[10px] text-zinc-600 normal-case tracking-normal">
            {form.desires.length}/15 · tap to toggle
          </span>
        </label>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="profile-desires-picker">
          {desiresCatalog.map((tag) => {
            const active = form.desires.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleDesire(tag)}
                data-testid={`desire-toggle-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-black/40 border-white/10 text-zinc-300 hover:border-white/25 hover:text-white"
                }`}
              >
                {tag}
              </button>
            );
          })}
        </div>
        <div className="text-[10px] text-zinc-600 mt-2">
          Only tags from this list are stored — no free-text. Visible on your public profile.
        </div>
      </div>

      {/* ---------------- MY TAGS (free-text keywords) ---------------- */}
      <Section icon={SectionIcons.Sparkles} title="My Tags">
        <TagInput
          value={form.tags}
          onChange={(tags) => set({ tags })}
          placeholder="Add keywords to get found easier"
        />
      </Section>

      {/* ---------------- STATS ---------------- */}
      <Section icon={SectionIcons.Ruler} title="Stats">
        <Row
          label="Show age"
          right={<Toggle checked={form.show_age} onChange={(v) => set({ show_age: v })} testId="profile-toggle-show-age" />}
        />
        <Row
          label="Age"
          right={
            <InlineInput
              value={form.age}
              onChange={(v) => set({ age: v.replace(/[^0-9]/g, "").slice(0, 3) })}
              placeholder="—"
              type="tel"
              testId="profile-input-age"
              className="max-w-[80px]"
            />
          }
        />
        <Row
          label="Height (cm)"
          right={
            <InlineInput
              value={form.height_cm}
              onChange={(v) => set({ height_cm: v.replace(/[^0-9]/g, "").slice(0, 3) })}
              placeholder="—"
              type="tel"
              testId="profile-input-height"
              className="max-w-[80px]"
            />
          }
        />
        <Row
          label="Weight (kg)"
          right={
            <InlineInput
              value={form.weight_kg}
              onChange={(v) => set({ weight_kg: v.replace(/[^0-9]/g, "").slice(0, 3) })}
              placeholder="—"
              type="tel"
              testId="profile-input-weight"
              className="max-w-[80px]"
            />
          }
        />
        <Row
          label="Body type"
          right={<InlineSelect value={form.body_type} onChange={(v) => set({ body_type: v })} options={BODY_TYPES} testId="profile-select-body-type" />}
        />
        <Row
          label="Show position"
          right={<Toggle checked={form.show_position} onChange={(v) => set({ show_position: v })} testId="profile-toggle-show-position" />}
        />
        <Row
          label="Position"
          right={<InlineSelect value={form.position} onChange={(v) => set({ position: v })} options={POSITIONS} testId="profile-select-position" />}
        />
        <Row
          label="Ethnicity"
          right={<InlineSelect value={form.ethnicity} onChange={(v) => set({ ethnicity: v })} options={ETHNICITIES} testId="profile-select-ethnicity" />}
        />
        <Row
          label="Relationship"
          right={<InlineSelect value={form.relationship_status} onChange={(v) => set({ relationship_status: v })} options={RELATIONSHIP_STATUSES} testId="profile-select-relationship" />}
        />
      </Section>

      {/* ---------------- PREFERENCES ---------------- */}
      <Section icon={SectionIcons.Users2} title="Preferences">
        <Row
          label="Show my tribes"
          right={<Toggle checked={form.show_tribes} onChange={(v) => set({ show_tribes: v })} testId="profile-toggle-show-tribes" />}
        />
        <Row label="My Tribes" hint={`${form.my_tribes.length} selected`} />
        <ChipMultiSelect
          options={TRIBES}
          value={form.my_tribes}
          onChange={(my_tribes) => set({ my_tribes })}
          testId="profile-chips-my-tribes"
        />
        <Row label="Tribes I'm Into" hint={`${form.tribes_into.length} selected`} />
        <ChipMultiSelect
          options={TRIBES}
          value={form.tribes_into}
          onChange={(tribes_into) => set({ tribes_into })}
          testId="profile-chips-tribes-into"
        />
        <Row label="Looking For" hint={`${form.looking_for.length} selected`} />
        <ChipMultiSelect
          options={LOOKING_FOR_OPTIONS}
          value={form.looking_for}
          onChange={(looking_for) => set({ looking_for })}
          testId="profile-chips-looking-for"
        />
        <Row label="Meet At" hint={`${form.meet_at.length} selected`} />
        <ChipMultiSelect
          options={MEET_AT_OPTIONS}
          value={form.meet_at}
          onChange={(meet_at) => set({ meet_at })}
          testId="profile-chips-meet-at"
        />
        <Row
          label="Accepts non-explicit photos"
          right={<Toggle checked={form.accepts_nsfw} onChange={(v) => set({ accepts_nsfw: v })} testId="profile-toggle-nsfw" />}
        />
      </Section>

      {/* ---------------- TRIPS ---------------- */}
      <TripsSection />

      {/* ---------------- AMBASSADOR ---------------- */}
      <AmbassadorSection />

      {/* ---------------- NOTIFICATIONS ---------------- */}
      <NotificationsSection />

      {/* ---------------- PRIVACY ---------------- */}
      <Section icon={SectionIcons.Shield} title="Privacy">
        <VipBadgeVisibilityRow />
        <Link to="/blocked" data-testid="profile-privacy-blocked-members" className="flex items-center justify-between px-4 py-3.5 gap-3 hover:bg-white/[0.03] transition-colors">
          <div className="min-w-0">
            <div className="text-[15px] text-white flex items-center gap-2"><Ban size={14} className="text-zinc-400" /> Blocked members</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">See who you&apos;ve blocked and unblock them</div>
          </div>
          <ChevronRight size={16} className="text-zinc-500 shrink-0" />
        </Link>
      </Section>

      {/* ---------------- IDENTITY ---------------- */}
      <Section icon={SectionIcons.User} title="Identity">
        <Row
          label="Gender"
          right={<InlineSelect value={form.gender} onChange={(v) => set({ gender: v })} options={GENDERS} testId="profile-select-gender" />}
        />
        <Row
          label="Pronouns"
          right={<InlineSelect value={form.pronouns} onChange={(v) => set({ pronouns: v })} options={PRONOUNS} testId="profile-select-pronouns" />}
        />
      </Section>

      {/* ---------------- HEALTH ---------------- */}
      <Section icon={SectionIcons.HeartPulse} title="Health">
        <Row
          label="HIV Status"
          right={<InlineSelect value={form.hiv_status} onChange={(v) => set({ hiv_status: v })} options={HIV_STATUSES} testId="profile-select-hiv" />}
        />
        <Row
          label="Last Tested Date"
          hint={form.hiv_status ? null : "Enter your HIV status to add a last tested date"}
          right={
            <InlineInput
              type="date"
              value={form.last_tested_date}
              onChange={(v) => set({ last_tested_date: v })}
              testId="profile-input-tested-date"
            />
          }
        />
      </Section>

      {/* ---------------- SOCIAL LINKS ---------------- */}
      <Section icon={SectionIcons.Share2} title="Social Links">
        <Row
          label="Instagram"
          right={<InlineInput value={form.instagram_handle} onChange={(v) => set({ instagram_handle: v })} placeholder="@username" testId="profile-input-instagram" />}
        />
        <Row
          label="X"
          right={<InlineInput value={form.x_handle} onChange={(v) => set({ x_handle: v })} placeholder="@username" testId="profile-input-x" />}
        />
        <Row
          label="Facebook"
          right={<InlineInput value={form.facebook_handle} onChange={(v) => set({ facebook_handle: v })} placeholder="username" testId="profile-input-facebook" />}
        />
        <Row
          label="Spotify"
          right={<InlineInput value={form.spotify_handle} onChange={(v) => set({ spotify_handle: v })} placeholder="username" testId="profile-input-spotify" />}
        />
        {/* Iter59 — TikTok + Snapchat added per user request. */}
        <Row
          label="TikTok"
          right={<InlineInput value={form.tiktok_handle} onChange={(v) => set({ tiktok_handle: v })} placeholder="@username" testId="profile-input-tiktok" />}
        />
        <Row
          label="Snapchat"
          right={<InlineInput value={form.snapchat_handle} onChange={(v) => set({ snapchat_handle: v })} placeholder="username" testId="profile-input-snapchat" />}
        />
      </Section>

      {/* ---------------- SECURITY (existing) ---------------- */}
      <div className="mt-6 space-y-4">
        <GoogleLinkBlock googleLinked={profile.google_linked} />
        <TwoFactorBlock
          enabled={!!profile.two_factor_enabled}
          onToggled={onTwoFactorToggled}
        />
      </div>

      {/* ---------------- ACTIONS ---------------- */}
      <div className="mt-8 pt-4 border-t border-white/5 flex items-center gap-3 sticky bottom-0 bg-black/80 backdrop-blur-md -mx-6 px-6 py-4">
        <Button data-testid={PROFILE.saveButton} onClick={onSave} className="btn-crimson rounded-none">
          Save
        </Button>
        <Button onClick={onCancel} variant="ghost" className="rounded-none text-zinc-400 hover:text-white">
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Free-text tag input — comma or enter adds; chips are removable. */
function TagInput({ value, onChange, placeholder }) {
  const commit = (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    if (value.includes(t) || value.length >= 20) return;
    onChange([...value, t]);
  };
  return (
    <div className="px-4 py-3">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="profile-tags-chips">
          {value.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 text-xs bg-yellow-400/15 border border-yellow-400/30 text-yellow-200 rounded-full pl-3 pr-1 py-1"
            >
              {t}
              <button
                onClick={() => onChange(value.filter((x) => x !== t))}
                className="w-4 h-4 rounded-full hover:bg-yellow-400/30 flex items-center justify-center text-yellow-200"
                data-testid={`profile-tag-remove-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        placeholder={placeholder}
        data-testid="profile-tag-input"
        className="w-full bg-transparent text-[15px] text-white placeholder-zinc-500 focus:outline-none"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(e.currentTarget.value);
            e.currentTarget.value = "";
          }
        }}
        onBlur={(e) => {
          commit(e.currentTarget.value);
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}

/** Google-linking row inside the edit form. */
function GoogleLinkBlock({ googleLinked }) {
  return (
    <div className="border-t border-white/5 pt-4 mt-2" data-testid="profile-edit-google-block">
      <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-2">Google sign-in</div>
      {googleLinked ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <Check size={14} /> Linked — you can log in with Google anytime.
        </div>
      ) : isNativeIOS() ? (
        <p className="text-xs text-zinc-500 leading-relaxed">
          Google account linking is available on the myPandaX website.
        </p>
      ) : (
        <>
          <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
            Link your Google account so you can log back in with one tap — no password needed. Your existing email + password will keep working.
          </p>
          <Button
            type="button"
            onClick={() => {
              try { window.sessionStorage.setItem("pandax_google_intent", "link"); } catch (err) { console.debug("session-storage unavailable:", err?.name); }
              const redirect = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
            }}
            variant="outline"
            className="rounded-none bg-transparent border border-white/15 text-white hover:border-rose-600 hover:bg-white/[0.03] flex items-center gap-2"
            data-testid="profile-link-google-button"
          >
            <GoogleGlyph size={14} /> Link Google
          </Button>
        </>
      )}
    </div>
  );
}
